/**
 * RTK Query API for Computer-Based Assessments
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './config';
import { 
  AssessmentStatus, 
  AssessmentPurpose, 
  QuestionType, 
  DifficultyLevel, 
  SubmissionStatus 
} from '../lib/constants';

export type GenerationState =
  | 'IDLE'
  | 'QUEUED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'PARTIAL'
  | 'FAILED';

const DECIMAL_SCORE_FIELDS = ['totalScore', 'percentage', 'assessmentTotalScore'] as const;

// The API serialises DecimalFields as strings ("61.00"); the typed model promises numbers.
const normalizeSubmissionScores = (submissions: StudentSubmission[]): StudentSubmission[] =>
  submissions.map(submission => {
    const normalized: Record<string, unknown> = { ...submission };
    DECIMAL_SCORE_FIELDS.forEach(field => {
      if (normalized[field] != null) normalized[field] = Number(normalized[field]);
    });
    return normalized as unknown as StudentSubmission;
  });

export interface ComputerAssessment {
  id: number;
  title: string;
  topic: string;
  description?: string;
  instructions?: string;
  learningObjectives?: string;
  classroom: {
    id: number;
    displayName: string;
    grade: number;
  };
  subject: {
    id: number;
    displayName: string;
  };
  teacher: {
    id: number;
    displayName: string;
  };
  numQuestions: number;
  totalScore: number;
  timeLimitMinutes?: number;
  retryAttempts: number;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showCorrectAnswers: boolean;
  startDate: string;
  dueDate: string;
  allowLateSubmission: boolean;
  status: typeof AssessmentStatus[keyof typeof AssessmentStatus];
  generationState?: GenerationState;
  generationSavedCount?: number | null;
  generationMessage?: string;
  generationStartedAt?: string | null;
  purpose: typeof AssessmentPurpose[keyof typeof AssessmentPurpose];
  assignmentType: 'HOMEWORK' | 'CLASSWORK' | 'QUIZ' | 'PROJECT' | 'EXAM';
  assignmentId?: number;
  assignmentTitle?: string;
  createdBy?: {
    id: number;
    displayName: string;
  };
  created: string;
  modified: string;
  actions: {
    canDelete: boolean;
    canEdit: boolean;
  };
  isGraded: boolean;
  isPractice: boolean;
  isActive: boolean;
  isPublished: boolean;
  publicUid: string;
  lessonId?: number;
  assignedStudentsCount?: number;
  assignedStudents?: Array<{ id: number; displayName: string }>;
  averageScore?: number;
  pinCode?: string;
  requiresPin?: boolean;
  allowBacktracking?: boolean;
}

export interface Question {
  id: number;
  questionText: string;
  questionType: typeof QuestionType[keyof typeof QuestionType];
  isChoiceResponse?: boolean;
  isTextResponse?: boolean;
  difficulty: typeof DifficultyLevel[keyof typeof DifficultyLevel];
  points: number;
  order: number;
  explanation?: string;
  imageUrl?: string;
  isAiGenerated: boolean;
  approved: boolean;
  options?: QuestionOption[];
}

export interface BulkImportQuestionsResponse {
  created: number;
  questions: Question[];
}

export interface QuestionOption {
  id: number;
  optionText: string;
  isCorrect: boolean;
  order: number;
  feedback?: string;
}

export interface StudentSubmission {
  id: number;
  assessmentId: number;
  assessmentTitle: string;
  assessmentTopic: string;
  assessmentTotalScore: number;
  student: number;
  studentName: string;
  attemptNumber: number;
  startedAt: string;
  submittedAt?: string;
  timeTakenSeconds?: number;
  status: typeof SubmissionStatus[keyof typeof SubmissionStatus];
  totalScore: number;
  percentage: number;
  numCorrect: number;
  numIncorrect: number;
  numUnanswered: number;
  isCompleted: boolean;
  isLate: boolean;
  deadlineAt?: string | null;
  remainingSeconds?: number | null;
  existingAnswers?: Array<{
    questionId: number;
    selectedOptionId?: number | null;
    answerText?: string | null;
    timeSpentSeconds?: number;
  }>;
}

export interface ProgressRow {
  student: number;
  studentName: string;
  attemptNumber: number;
  status: typeof SubmissionStatus[keyof typeof SubmissionStatus] | 'NOT_STARTED';
  startedAt: string | null;
  deadlineAt: string | null;
  remainingSeconds: number | null;
  answeredCount: number;
  totalQuestions: number;
  lastActivityAt: string | null;
}

export interface AssessmentProgress {
  summary: {
    notStarted: number;
    inProgress: number;
    submitted: number;
    graded: number;
  };
  students: ProgressRow[];
}

export interface AssessmentAnalytics {
  overview: {
    avgScore: number;
    passRate: number;
    completedCount: number;
    totalStudents: number;
    avgTime: string;
  };
  scoreDistribution: { labels: string[]; data: number[] };
  questionStats: { labels: string[]; data: number[] };
  strugglingStudents: Array<{ name: string; score: number }>;
  aiInsights: string;
}

export interface SubmissionAnswer {
  id: number;
  question: number;
  questionText: string;
  questionType: string;
  questionPoints: number;
  selectedOption?: number;
  selectedOptionText?: string;
  answerText?: string;
  isCorrect?: boolean;
  pointsEarned: number;
  aiFeedback?: string;
  timeSpentSeconds: number;
}

export interface CreateAssessmentRequest {
  title: string;
  topic: string;
  description?: string;
  instructions?: string;
  learningObjectives?: string;
  classroomId: number;
  subjectId: number;
  teacherId?: number;
  sessionId?: number;
  lessonId?: number;
  numQuestions: number;
  totalScore: number;
  timeLimitMinutes?: number;
  retryAttempts: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  showCorrectAnswers?: boolean;
  startDate: string;
  dueDate: string;
  allowLateSubmission?: boolean;
  purpose: 'PRACTICE' | 'GRADED';
  assignmentType?: 'HOMEWORK' | 'CLASSWORK' | 'QUIZ' | 'PROJECT' | 'EXAM';
  assignedStudentIds?: number[];
  pinCode?: string;
}

export interface CreateQuestionRequest {
  assessmentId: number;
  questionText: string;
  questionType: string;
  difficulty: string;
  points: number;
  order?: number;
  explanation?: string;
  imageUrl?: string;
  options?: Array<{
    id?: number;
    optionText: string;
    isCorrect: boolean;
    order?: number;
    feedback?: string;
  }>;
}

export interface PublishAssessmentRequest {
  examinationId?: number;
}

export interface ConvertToGradedRequest {
  examinationId?: number;
  assignmentType: 'HOMEWORK' | 'CLASSWORK' | 'QUIZ' | 'PROJECT' | 'EXAM';
  useBestScore: boolean;
}

export interface StartSubmissionRequest {
  assessmentId: string | number;
  pinCode?: string;
}

export interface SubmitAnswerRequest {
  questionId: number;
  selectedOptionId?: number;
  answerText?: string;
  timeSpentSeconds?: number;
}

export const assessmentsApi = createApi({
  reducerPath: 'assessmentsApi',

  baseQuery,
  tagTypes: ['Assessment', 'Question', 'Submission', 'Progress'],
  endpoints: builder => ({
    // Staff Assessment endpoints
    getAssessments: builder.query<ComputerAssessment[], {
      sessionId?: number;
      classId?: number;
      subjectId?: number;
      lessonId?: number;
      status?: string;
      purpose?: string;
      q?: string;
    }>({
      query: params => ({
        url: '/assessments/computer/',
        params,
      }),
      transformResponse: (response: { data: ComputerAssessment[] }) => response.data,
      providesTags: ['Assessment'],
    }),

    getAssessment: builder.query<ComputerAssessment, string | number>({
      query: id => `/assessments/computer/${id}/`,
      providesTags: ['Assessment'],
    }),

    // Student Portal endpoints
    getStudentAssessments: builder.query<ComputerAssessment[], { status?: string }>({
      query: params => ({
        url: '/assessments/student/assessments/',
        params,
      }),
      transformResponse: (response: { data: ComputerAssessment[] }) => response.data,
      providesTags: ['Assessment'],
    }),

    getStudentAssessment: builder.query<ComputerAssessment, string | number>({
      query: id => `/assessments/student/assessments/${id}/`,
      providesTags: ['Assessment'],
    }),

    getStudentQuestions: builder.query<Question[], string | number>({
        query: id => `/assessments/student/assessments/${id}/questions/`,
        transformResponse: (response: { data: Question[] }) => response.data,
        providesTags: ['Question'],
    }),

    // ... create, update, delete, publish, convertToGraded, generateQuestions (Staff only)
    createAssessment: builder.mutation<ComputerAssessment, CreateAssessmentRequest>({
      query: data => ({
        url: '/assessments/computer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Assessment'],
    }),

    updateAssessment: builder.mutation<ComputerAssessment, { id: number; data: Partial<CreateAssessmentRequest> & { lessonId?: number | null } }>({
      query: ({ id, data }) => ({
        url: `/assessments/computer/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Assessment'],
    }),

    deleteAssessment: builder.mutation<void, number>({
      query: id => ({
        url: `/assessments/computer/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Assessment'],
    }),
    
    deleteQuestion: builder.mutation<void, number>({
      query: id => ({
        url: `/assessments/questions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Question', 'Assessment'],
    }),

    createQuestion: builder.mutation<Question, CreateQuestionRequest>({
      query: data => ({
        url: '/assessments/questions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Question', 'Assessment'],
    }),

    updateQuestion: builder.mutation<Question, { id: number; data: Partial<CreateQuestionRequest> }>({
      query: ({ id, data }) => ({
        url: `/assessments/questions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Question', 'Assessment'],
    }),

    publishAssessment: builder.mutation<ComputerAssessment, { id: number; data: PublishAssessmentRequest }>({
      query: ({ id, data }) => ({
        url: `/assessments/computer/${id}/publish/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Assessment'],
    }),

    convertToGraded: builder.mutation<{
      assessment: ComputerAssessment;
      assignmentId: number;
      scoresSynced: number;
      message: string;
    }, { id: number; data: ConvertToGradedRequest }>({
      query: ({ id, data }) => ({
        url: `/assessments/computer/${id}/convert-to-graded/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Assessment'],
    }),

    generateQuestions: builder.mutation<ComputerAssessment, number>({
      query: id => ({
        url: `/assessments/computer/${id}/generate-questions/`,
        method: 'POST',
      }),
      invalidatesTags: ['Assessment', 'Question'],
    }),

    bulkImportQuestions: builder.mutation<BulkImportQuestionsResponse, { assessmentId: number; file: File }>({
      query: ({ assessmentId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/assessments/computer/${assessmentId}/bulk-import-questions/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Question', 'Assessment'],
    }),

    getQuestions: builder.query<Question[], string | number>({
        query: id => `/assessments/computer/${id}/questions/`,
        transformResponse: (response: { data: Question[] }) => response.data,
        providesTags: ['Question'],
    }),

    // Staff Submission endpoints
    getSubmissions: builder.query<StudentSubmission[], {
      assessmentId?: number;
      studentId?: number;
      status?: string;
    }>({
      query: params => ({
        url: '/assessments/submissions/',
        params,
      }),
      transformResponse: (response: { data: StudentSubmission[] }) => normalizeSubmissionScores(response.data),
      providesTags: ['Submission'],
    }),

    getSubmission: builder.query<StudentSubmission, number>({
      query: id => `/assessments/submissions/${id}/`,
      providesTags: ['Submission'],
    }),

    resetSubmission: builder.mutation<StudentSubmission, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/assessments/submissions/${id}/reset/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Submission', 'Progress'],
    }),

    getSubmissionAnswers: builder.query<SubmissionAnswer[], number>({
        query: id => `/assessments/submissions/${id}/answers/`,
        transformResponse: (response: { data: SubmissionAnswer[] }) => response.data,
        providesTags: ['Submission'],
    }),

    // Student Submission Portal endpoints
    getStudentSubmissions: builder.query<StudentSubmission[], any>({
      query: params => ({
        url: '/assessments/student/submissions/',
        params,
      }),
      transformResponse: (response: { data: StudentSubmission[] }) => normalizeSubmissionScores(response.data),
      providesTags: ['Submission'],
    }),

    getStudentSubmission: builder.query<StudentSubmission, number>({
      query: id => `/assessments/student/submissions/${id}/`,
      providesTags: ['Submission'],
    }),

    startSubmission: builder.mutation<StudentSubmission, StartSubmissionRequest>({
      query: data => ({
        url: '/assessments/student/submissions/start/',
        method: 'POST',
        body: {
          assessmentId: data.assessmentId,
          pinCode: data.pinCode
        },
      }),
      invalidatesTags: ['Submission'],
    }),

    submitAnswer: builder.mutation<SubmissionAnswer, { id: number; data: SubmitAnswerRequest }>({
      query: ({ id, data }) => ({
        url: `/assessments/student/submissions/${id}/submit-answer/`,
        method: 'POST',
        body: data,
      }),
    }),

    completeSubmission: builder.mutation<StudentSubmission, number>({
      query: id => ({
        url: `/assessments/student/submissions/${id}/complete/`,
        method: 'POST',
      }),
      invalidatesTags: ['Submission'],
    }),

    getStudentResults: builder.query<{
      submission: StudentSubmission;
      answers: SubmissionAnswer[];
      showCorrectAnswers: boolean;
    }, number>({
      query: id => `/assessments/student/submissions/${id}/results/`,
    }),

    getAssessmentAnalytics: builder.query<AssessmentAnalytics, number>({
        query: id => `/assessments/computer/${id}/analytics/`,
    }),

    getAssessmentProgress: builder.query<AssessmentProgress, number>({
        query: id => `/assessments/computer/${id}/progress/`,
        providesTags: ['Progress'],
    }),

    studentLogin: builder.mutation<any, { code: string; deviceId?: string; geoLocation?: any }>({
        query: (data) => ({
            url: `/auth/student`,
            method: 'POST',
            body: data,
        }),
    }),
    getSubmissionResults: builder.query<{
      submission: StudentSubmission;
      answers: SubmissionAnswer[];
      showCorrectAnswers: boolean;
    }, number>({
      query: id => `/assessments/submissions/${id}/results/`,
    }),
  }),
});

export const {
  // Assessment hooks (Staff)
  useGetAssessmentsQuery,
  useGetAssessmentQuery,
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
  useDeleteAssessmentMutation,
  usePublishAssessmentMutation,
  useConvertToGradedMutation,
  useGenerateQuestionsMutation,
  useGetQuestionsQuery,
  useBulkImportQuestionsMutation,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useGetAssessmentAnalyticsQuery,
  useGetAssessmentProgressQuery,

  // Student Portal hooks
  useGetStudentAssessmentsQuery,
  useGetStudentAssessmentQuery,
  useGetStudentQuestionsQuery,
  useGetStudentSubmissionsQuery,
  useGetStudentSubmissionQuery,
  useGetStudentResultsQuery,

  // Submission hooks (Staff)
  useGetSubmissionsQuery,
  useGetSubmissionQuery,
  useGetSubmissionAnswersQuery,
  useGetSubmissionResultsQuery,
  useResetSubmissionMutation,

  // Submission hooks (Student)
  useStartSubmissionMutation,
  useSubmitAnswerMutation,
  useCompleteSubmissionMutation,

  // Auth
  useStudentLoginMutation,
} = assessmentsApi;
